import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import {
  PATH_RESOLVABLE_SERVICE_COSTS,
  PATH_RESOLVABLE_SERVICE_COSTS_EDIT,
} from '../../../app/constants/paths.constant';
import { RightDrawer } from '../../../components/RightDrawer';
import { SpeedDial } from '../../../components/SpeedDial';
import { FormVariant } from '../../../forms';
import {
  ResolvableServiceForm,
  ResolvableServiceFormTab,
} from '../../../forms/ResolvableService';
import { useBuildPaths } from '../../../hooks/useBuildPaths.hook';

import { List } from './List';

export const ResolvableServicePage_Costs_ResolvableServiceBaseInterface_Fragment =
  graphql(`
    fragment ResolvableServicePage_Costs_ResolvableServiceBaseInterface_Fragment on ResolvableServiceBaseInterface {
      _id
      costUsages {
        ...ResolvableServicePage_CostUsagesList_CostUsage_Fragment
      }
      ...ResolvableServiceForm_ResolvableServiceBaseInterface_Fragment
    }
  `);

interface Props {
  readonly resolvableService: FragmentType<
    typeof ResolvableServicePage_Costs_ResolvableServiceBaseInterface_Fragment
  >;
}

export const Costs = ({
  resolvableService: pResolvableService,
}: Readonly<Props>) => {
  const resolvableService = getFragmentData(
    ResolvableServicePage_Costs_ResolvableServiceBaseInterface_Fragment,
    pResolvableService,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_RESOLVABLE_SERVICE_COSTS,
      edit: PATH_RESOLVABLE_SERVICE_COSTS_EDIT,
    },
    { id: resolvableService._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  return (
    <>
      <List costUsages={resolvableService.costUsages} />
      <SpeedDial
        actions={[
          {
            id: 'editResolvableServiceCosts',
            icon: <EditIcon />,
            name: 'Modifier Coûts Liés',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Coûts Liés au Service"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <ResolvableServiceForm
          formVariant={FormVariant.Modification}
          initialValues={resolvableService}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          resolvableServiceId={resolvableService._id}
          stepIdsSubset={[
            ResolvableServiceFormTab.CostsSelection,
            ResolvableServiceFormTab.CostsOrder,
            ResolvableServiceFormTab.CostsUsage,
          ]}
          title="Formulaire de modification des Coûts Liés au Service"
        />
      </RightDrawer>
    </>
  );
};
